import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sun, Heart, BookOpen, Sparkles, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

export default function Landing() {
    const navigate = useNavigate();
    const { login, register, user } = useAuth();
    const [showAuth, setShowAuth] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Form states
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');

    // Redirect if already logged in
    if (user) {
        navigate('/dashboard');
        return null;
    }

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login(loginEmail, loginPassword);
            toast.success('Welcome back! May your day be blessed.');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await register(registerName, registerEmail, registerPassword);
            toast.success('Welcome to Lumina! Your faith journey begins.');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
            {/* Hero Section */}
            <div className="hero-section flex-1">
                <div 
                    className="hero-background"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1764410510172-2993b2c9c04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxvcGVuJTIwYmlibGUlMjBzdW5saWdodCUyMGNhbG0lMjBvY2VhbiUyMHN1bnJpc2UlMjBnb2xkZW4lMjB3aGVhdCUyMGZpZWxkfGVufDB8fHx8MTc2NzI3OTcwMHww&ixlib=rb-4.1.0&q=85')`
                    }}
                />
                <div className="hero-overlay" />
                
                <div className="hero-content max-w-4xl mx-auto">
                    {!showAuth ? (
                        <div className="animate-fade-in">
                            {/* Logo/Brand */}
                            <div className="mb-8">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                                    <Sun className="w-4 h-4" />
                                    <span className="text-sm font-medium tracking-wide uppercase">Faith Sanctuary</span>
                                </div>
                                <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight text-[#2D2A26] mb-4">
                                    Lumina
                                </h1>
                                <p className="font-accent text-xl md:text-2xl text-[#8C867D] italic mb-2">
                                    "Thy word is a lamp unto my feet, and a light unto my path."
                                </p>
                                <p className="text-sm text-muted-foreground">— Psalm 119:105 (KJV)</p>
                            </div>

                            {/* Feature highlights */}
                            <div className="flex flex-wrap justify-center gap-6 mb-12">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <BookOpen className="w-5 h-5 text-primary" />
                                    <span>Daily Devotionals</span>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Heart className="w-5 h-5 text-primary" />
                                    <span>Mood Tracking</span>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    <span>Spiritual Journaling</span>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <Button 
                                data-testid="begin-journey-btn"
                                onClick={() => setShowAuth(true)}
                                className="btn-primary text-lg px-10 py-6 h-auto"
                            >
                                Begin Your Journey
                            </Button>

                            <p className="mt-6 text-sm text-muted-foreground">
                                Start your day with faith, hope, and inspiration
                            </p>
                        </div>
                    ) : (
                        <div className="auth-card animate-fade-in">
                            <button 
                                onClick={() => setShowAuth(false)}
                                className="text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2 text-sm"
                            >
                                ← Back
                            </button>
                            
                            <h2 className="font-display text-2xl md:text-3xl font-medium text-[#2D2A26] mb-2">
                                Welcome
                            </h2>
                            <p className="text-muted-foreground mb-8">
                                Enter your sanctuary of faith
                            </p>

                            <Tabs defaultValue="login" className="w-full">
                                <TabsList className="grid w-full grid-cols-2 mb-6">
                                    <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                                    <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
                                </TabsList>

                                <TabsContent value="login">
                                    <form onSubmit={handleLogin} className="space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="login-email">Email</Label>
                                            <Input
                                                id="login-email"
                                                data-testid="login-email-input"
                                                type="email"
                                                placeholder="your@email.com"
                                                value={loginEmail}
                                                onChange={(e) => setLoginEmail(e.target.value)}
                                                required
                                                className="bg-white/50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password">Password</Label>
                                            <div className="relative">
                                                <Input
                                                    id="login-password"
                                                    data-testid="login-password-input"
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="••••••••"
                                                    value={loginPassword}
                                                    onChange={(e) => setLoginPassword(e.target.value)}
                                                    required
                                                    className="bg-white/50 pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        <Button 
                                            type="submit" 
                                            data-testid="login-submit-btn"
                                            className="w-full btn-primary"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Signing in...' : 'Sign In'}
                                        </Button>
                                    </form>
                                </TabsContent>

                                <TabsContent value="register">
                                    <form onSubmit={handleRegister} className="space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="register-name">Your Name</Label>
                                            <Input
                                                id="register-name"
                                                data-testid="register-name-input"
                                                type="text"
                                                placeholder="John"
                                                value={registerName}
                                                onChange={(e) => setRegisterName(e.target.value)}
                                                required
                                                className="bg-white/50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-email">Email</Label>
                                            <Input
                                                id="register-email"
                                                data-testid="register-email-input"
                                                type="email"
                                                placeholder="your@email.com"
                                                value={registerEmail}
                                                onChange={(e) => setRegisterEmail(e.target.value)}
                                                required
                                                className="bg-white/50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-password">Password</Label>
                                            <div className="relative">
                                                <Input
                                                    id="register-password"
                                                    data-testid="register-password-input"
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="••••••••"
                                                    value={registerPassword}
                                                    onChange={(e) => setRegisterPassword(e.target.value)}
                                                    required
                                                    minLength={6}
                                                    className="bg-white/50 pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        <Button 
                                            type="submit" 
                                            data-testid="register-submit-btn"
                                            className="w-full btn-primary"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Creating Account...' : 'Create Account'}
                                        </Button>
                                    </form>
                                </TabsContent>
                            </Tabs>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
